import preventDefault from 'volta/helpers/prevent-default';
import stopPropagation from 'volta/helpers/stop-propagation';
import tElse from 'volta/helpers/t-else';
import { SearchEntryResourceName } from '../../models/search-entry';
import BvBadge from 'volta/components/bv-badge';
import BvCard from 'volta/components/bv-card';
import BvEmptySearchResult from 'volta/components/bv-empty-search-result';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvModal from 'volta/components/bv-modal';
import BvResourceList from 'volta/components/bv-resource-list';
import BvSpinner from 'volta/components/bv-spinner';
import BvText from 'volta/components/bv-text';
import Default from './entry/default';
import History from './entry/history';
import Menu from './entry/menu';
import Sku from './entry/sku';
import User from './entry/user';
import SearchField from './search-field';
import { hash, fn, get } from '@ember/helper';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import includes from '@nullvoxpopuli/ember-composable-helpers/helpers/includes';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import t from 'ember-intl/helpers/t';
import onKey from 'ember-keyboard/modifiers/on-key';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import not from 'ember-truth-helpers/helpers/not';
import lowerCase from 'lodash/lowerCase';
import { getPartialEntityTypesOptions } from 'volta/models/constants/entity-types';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter, inFilter } from 'volta/utils/filters-utils';
import { strNormalizeAccent } from 'volta/utils/text-utils';

import type SearchEntry from '../../models/search-entry';
import type RouterService from '@ember/routing/router-service';
import type SkuModel from 'volta/models/sku';
import type { TEntityType } from 'volta/models/types/entity-types';
import type { IHistoryRecord } from 'volta/routes/base';
import type { IBaseMenuItem } from 'volta/services/app-nav-state';
import type AppNavState from 'volta/services/app-nav-state';
import type StoreService from 'volta/services/store';
import type { TError } from 'volta/utils/error-utils';

export const TYPING_DEBOUNCE_MS = 300;

export interface BvAppSearchSignature {
  Args: {
    historyItems?: IHistoryRecord[];
    skuItems?: SkuModel[];
    menuItems?: IBaseMenuItem[];
    onDismiss?: () => void;
  };
}

type TSearchResultKey = 'mostVisited' | 'menu' | 'search';

interface ISearchResults extends Record<TSearchResultKey, unknown[]> {
  mostVisited: IHistoryRecord[];
  menu: (IBaseMenuItem & { id: string })[];
  search: SearchEntry[];
}

const InitialSearchResults: ISearchResults = {
  mostVisited: [],
  menu: [],
  search: []
};

const AllEntityTypes: TEntityType[] = ['skus', 'warehouses', 'suppliers', 'workshops', 'users'];

export default class BvAppSearchComponent extends Component<BvAppSearchSignature> {
  <template>
    <BvModal
      @isFocusTrapActive={{false}}
      @onClose={{optional @onDismiss}}
      @focusTrapOptions={{hash
        clickOutsideDeactivates=true
        initialFocus=false
        escapeDeactivates=false
        returnFocusOnDeactivate=false
      }}
      @backdropClassNames='bv-app-search-modal__backdrop'
      @size='search'
      class='bv-app-search-modal'
      as |modal|
    >
      <modal.Body @scrollable={{true}} @sectioned={{false}} class='bv-app-search-modal__body'>
        {{! template-lint-disable no-at-ember-render-modifiers }}
        <BvCard
          class='bv-app-search-results'
          {{didInsert (perform this.buildResults)}}
          {{didUpdate (perform this.buildResults) this.appNavState.searchText}}
          {{createRef 'resultsContainer' bucket=this}}
          {{onKey
            'ArrowDown'
            (preventDefault (stopPropagation (fn this.handleChangeSelected 'down')))
          }}
          {{onKey 'ArrowUp' (preventDefault (stopPropagation (fn this.handleChangeSelected 'up')))}}
          {{onKey 'Enter' (preventDefault (stopPropagation this.handleSelect))}}
          as |card|
        >
          <SearchField
            @query={{this.appNavState.searchText}}
            @onSearch={{this.handleSearch}}
            @onClear={{fn this.handleSearch ''}}
          />
          {{#if this.buildResults.isRunning}}
            <BvSpinner @size='sm' class='u-position-absolute u-top-0 u-full-width u-m-4' />
          {{/if}}
          {{#if this.results.mostVisited.length}}
            <card.Section @title={{t 'mostVisited'}} @fullWidth={{true}}>
              <BvResourceList @items={{this.results.mostVisited}} @itemIdPath='id' as |bv|>
                <bv.ResourceListItem
                  class='search-entry'
                  @href={{bv.item.url}}
                  @condensed={{true}}
                  @isCurrent={{eq bv.item.id this.selectedItemId}}
                  @onClick={{@onDismiss}}
                >
                  <History @history={{bv.item}} />
                </bv.ResourceListItem>
              </BvResourceList>
            </card.Section>
          {{/if}}
          {{#if this.results.menu.length}}
            <card.Section @title={{t 'menu'}} @fullWidth={{true}}>
              <BvResourceList @items={{this.results.menu}} as |bv|>
                <bv.ResourceListItem
                  class='search-entry'
                  @route={{bv.item.route}}
                  @routeQuery={{bv.item.queryParams}}
                  @condensed={{true}}
                  @isCurrent={{eq bv.item.id this.selectedItemId}}
                >
                  <Menu @menu={{bv.item}} />
                </bv.ResourceListItem>
              </BvResourceList>
            </card.Section>
          {{/if}}
          {{#if this.results.search.length}}
            <card.Section @title={{t 'searchEntries'}} @fullWidth={{true}}>
              <BvResourceList
                @items={{this.results.search}}
                @loading={{this.searchTask.isRunning}}
                as |bv|
              >
                {{#let (get this.entityTypeOptions bv.item.entityType) as |etOption|}}
                  <bv.ResourceListItem
                    @condensed={{true}}
                    @route={{bv.item.entityRoute}}
                    @routeModel={{bv.item.id}}
                    @onClick={{@onDismiss}}
                    @isCurrent={{eq bv.item.id this.selectedItemId}}
                    {{! @glint-ignore: not typesafe yet }}
                    @media={{component
                      BvIcon
                      glyph=etOption.glyph
                      size='md'
                      hasBackdrop=true
                      color='text-subdued'
                      class='u-mt-2'
                    }}
                    class='search-entry'
                  >
                    {{#if (eq bv.item.entityType 'skus')}}
                      <Sku @entry={{bv.item}} />
                    {{else if (eq bv.item.entityType 'users')}}
                      <User @entry={{bv.item}} />
                    {{else}}
                      <Default @entry={{bv.item}} />
                    {{/if}}
                  </bv.ResourceListItem>
                {{/let}}
              </BvResourceList>
            </card.Section>
          {{/if}}
          {{#if
            (and
              (not this.results.search.length)
              (not this.results.menu.length)
              (not this.buildResults.isRunning)
              this.appNavState.hasSearchText
            )
          }}
            <card.Section>
              <BvEmptySearchResult
                @title={{t
                  'bvList.emptySearchResultTitle'
                  resourceNamePlural=(lowerCase (t 'searchEntries'))
                }}
                @description={{t 'bvList.emptySearchResultDescription'}}
              />
            </card.Section>
          {{else}}
            <card.Footer
              class='u-position-sticky u-bottom-0 u-z-1 u-border-top-divider u-py-2 u-bg-surface'
            >
              <BvInline @gap='2' @blockAlign='center' @align='space-between' class='u-full-width'>
                <BvText
                  @text={{t
                    (if
                      (eq this.allEntityTypes.length this.selectedEntityTypes.length)
                      'bvApp.searchResults.searchEverything'
                      'bvApp.searchResults.searchOnly'
                    )
                  }}
                  @color='subdued'
                  @variant='body-sm'
                />
                <BvInline @blockAlign='center' @gap='1'>
                  {{#each this.allEntityTypes as |entityType|}}
                    {{#let (includes entityType this.selectedEntityTypes) as |isSelected|}}
                      <BvBadge
                        @text={{tElse entityType}}
                        @outline={{isSelected}}
                        @subtle={{not isSelected}}
                        @onClick={{fn this.handleEntityTypeClick entityType (not isSelected)}}
                        class={{unless isSelected 'u-color-text-disabled'}}
                      />
                    {{/let}}
                  {{/each}}
                </BvInline>
              </BvInline>
            </card.Footer>
          {{/if}}
        </BvCard>
      </modal.Body>
    </BvModal>
  </template>
  @service appNavState!: AppNavState;
  @service router!: RouterService;
  @service store!: StoreService;

  @tracked results: ISearchResults = InitialSearchResults;
  @tracked pageSearchResults: IBaseMenuItem[] = [];
  @tracked searchEntries: SearchEntry[] = [];
  @tracked selectedEntityTypes: TEntityType[] = AllEntityTypes;
  @tracked selected: number = -1;

  @ref('resultsContainer') resultsContainer: HTMLElement | null = null;

  SearchEntryResourceName = SearchEntryResourceName;
  entityTypeOptions = getPartialEntityTypesOptions(AllEntityTypes);
  allEntityTypes = AllEntityTypes;

  get itemsNodes() {
    return (this.resultsContainer?.querySelectorAll('.search-entry') ?? []) as NodeListOf<
      HTMLButtonElement | HTMLAnchorElement
    >;
  }

  @cached
  get flattenSearchResults() {
    const { mostVisited, menu, search } = this.results;
    return [...mostVisited, ...menu, ...search];
  }

  get selectedItem() {
    return this.flattenSearchResults[this.selected];
  }

  get selectedItemId() {
    return this.selectedItem?.id;
  }

  @action
  handleSearch(query: string) {
    if (query !== this.appNavState.searchText) {
      this.appNavState.searchText = query;
    }
  }

  @action
  handleEntityTypeClick(entityType: TEntityType, isSelected: boolean) {
    this.selectedEntityTypes = isSelected
      ? [...this.selectedEntityTypes, entityType]
      : this.selectedEntityTypes.filter((et) => et !== entityType);
    this.buildResults.perform();
  }

  @action
  handleSelect() {
    const { selected, itemsNodes } = this;
    let index = 0;

    itemsNodes.forEach((_, i) => {
      if (i === selected) {
        index = i;
      }
    });

    const bvResoureceListItem = itemsNodes[index]?.getElementsByClassName(
      'bv-resource-list-item'
    )?.[0] as HTMLAnchorElement | HTMLButtonElement;

    if (bvResoureceListItem) {
      bvResoureceListItem.click();
      this.args.onDismiss?.();
    }
  }

  @action
  handleChangeSelected(direction?: 'up' | 'down') {
    const { selected, itemsNodes } = this;

    let index = 0;
    let newIndex = 0;

    if (direction === 'down') {
      itemsNodes.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = index === itemsNodes.length - 1 || selected < 0 ? 0 : index + 1;
    } else if (direction === 'up') {
      itemsNodes.forEach((_, i) => {
        if (i === selected) {
          index = i;
        }
      });

      newIndex = index ? index - 1 : itemsNodes.length - 1;
    } else {
      this.selected = 0;
    }

    const newItem: Element = itemsNodes[newIndex] as HTMLAnchorElement | HTMLButtonElement;

    if (newItem && typeof newIndex === 'number') {
      this.selected = newIndex;
      newItem.scrollIntoView({
        behavior: 'smooth',
        block: newIndex ? 'center' : 'end'
      });
    }
  }

  searchTask = restartableTask(async (q: string) => {
    try {
      const query = {
        page: {
          offset: 0,
          limit: 10
        },
        filter: {
          q: eqFilter(q),
          entityType: inFilter(this.selectedEntityTypes)
        }
      };
      await timeout(TYPING_DEBOUNCE_MS);
      this.searchEntries = (
        await this.store.query<SearchEntry>('search-entry', query)
      ).slice() as SearchEntry[];
      return this.searchEntries;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  buildResults = restartableTask(async () => {
    const {
      appNavState: { history, hasSearchText }
    } = this;

    try {
      if (hasSearchText) {
        this.searchMenu();
        await this.searchTask.perform(this.appNavState.searchText as string);
      }

      this.results = {
        mostVisited: hasSearchText ? [] : history.map((h) => ({ ...h, id: h.key })),
        menu: hasSearchText ? this.pageSearchResults.map((h) => ({ ...h, id: h.slug })) : [],
        search: hasSearchText ? this.searchEntries : []
      };
    } catch (error) {
      defaultErrorHandler(error as TError);
    }
  });

  private searchMenu() {
    const pageResults: IBaseMenuItem[] = [];
    const menuLabels = Object.keys(this.appNavState.searchMenuContent);

    menuLabels.forEach((label) => {
      const item = this.appNavState.searchMenuContent[label];
      if (
        item &&
        label.includes(strNormalizeAccent(this.appNavState.searchText?.toLowerCase() ?? ''))
      ) {
        pageResults.push(item);
      }
    });

    this.pageSearchResults = pageResults.length > 10 ? pageResults.slice(-5) : pageResults;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvAppSearch: typeof BvAppSearchComponent;
    'bv-app-search': typeof BvAppSearchComponent;
  }
}
